import { AccountCircleOutlined, DescriptionOutlined, HelpOutline } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Tooltip, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReactComponent as ShippoIcon } from '~/src/assets/icons/Shippo.svg';
import { useBasicMenu } from '~/src/hooks/useBasicMenu';

import { AccountMenu } from './AccountMenu';
import { DocumentationMenu } from './DocumentationMenu';
import { SupportMenu } from './SupportMenu';

const Header = styled('header')(({ theme }) => ({
  alignItems: 'center',
  background: '#1a3722',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
  },
}));

const NavigationContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(2),
}));

const NavigationIconButton = styled(IconButton)(({ theme }) => ({
  '&.active': {
    backgroundColor: `${theme.palette.common.white}20`,
    boxShadow: theme.shadows[1],
  },
  '&:hover': {
    backgroundColor: `${theme.palette.common.white}20`,
    boxShadow: theme.shadows[2],
  },
  borderRadius: theme.spacing(1),
  color: theme.palette.common.white,
}));

export const StyledMenu = styled(Menu)(({ theme }) => ({
  padding: '1rem',
  ul: {
    a: {
      height: theme.spacing(5),
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(1),
      paddingRight: theme.spacing(5),
    },
    li: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingRight: theme.spacing(4),
    },
    maxWidth: theme.spacing(35),
    width: theme.spacing(35),
  },
}));

export const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:last-child': {
    marginBottom: theme.spacing(2),
  },
  height: theme.spacing(5),
  justifyContent: 'flex-end',
  marginBottom: theme.spacing(1),
  paddingRight: theme.spacing(5),
})) as typeof MenuItem;

export const TruncatedText = styled(Typography)({
  overflow: 'hidden',
  textAlign: 'right',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
}) as typeof Typography;

export type Props = {
  anchorEl: HTMLElement | null;
  onCloseMenu: () => void;
  open: boolean;
};

export const NavigationBar = () => {
  const { t } = useTranslation('', { keyPrefix: 'navigationBar' });
  const { anchorEl, handleCloseMenu, handleOpenMenu, open } = useBasicMenu();
  const {
    anchorEl: supportAnchor,
    handleCloseMenu: supportCloseMenu,
    handleOpenMenu: supportOpenMenu,
    open: supportOpen,
  } = useBasicMenu();
  const {
    anchorEl: docAnchor,
    handleCloseMenu: docCloseMenu,
    handleOpenMenu: docOpenMenu,
    open: docOpen,
  } = useBasicMenu();

  return (
    <Header>
      <Link to="/">
        <ShippoIcon style={{ color: 'white', width: '2.61rem' }} />
      </Link>

      <NavigationContainer>
        <Tooltip title={t('documentation')}>
          <NavigationIconButton
            aria-controls={docOpen ? 'documentation-menu' : undefined}
            aria-expanded={docOpen ? 'true' : undefined}
            aria-haspopup="true"
            aria-label={t('documentation')}
            onClick={docOpenMenu}
            size="large"
          >
            <DescriptionOutlined />
          </NavigationIconButton>
        </Tooltip>

        <Tooltip title={t('support')}>
          <NavigationIconButton
            aria-controls={supportOpen ? 'support-menu' : undefined}
            aria-expanded={supportOpen ? 'true' : undefined}
            aria-haspopup="true"
            aria-label={t('support')}
            onClick={supportOpenMenu}
            size="large"
          >
            <HelpOutline />
          </NavigationIconButton>
        </Tooltip>

        <Tooltip title={t('account')}>
          <NavigationIconButton
            aria-controls={open ? 'account-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            aria-label={t('account')}
            onClick={handleOpenMenu}
            size="large"
          >
            <AccountCircleOutlined />
          </NavigationIconButton>
        </Tooltip>

        <AccountMenu anchorEl={anchorEl} onCloseMenu={handleCloseMenu} open={open} />
        <DocumentationMenu anchorEl={docAnchor} onCloseMenu={docCloseMenu} open={docOpen} />
        <SupportMenu anchorEl={supportAnchor} onCloseMenu={supportCloseMenu} open={supportOpen} />
      </NavigationContainer>
    </Header>
  );
};
